import type { ReactNode } from "react";

/**
 * AlertBanner — full-width contextual notice strip.
 *
 * Sits flush below the navbar (outside the page container). Use for
 * page-level notices that affect the whole view: hidden listings,
 * trial expiry warnings, verification prompts, etc.
 *
 * Usage:
 *   <AlertBanner variant="warning" icon={<EyeSlash … />}>
 *     <strong>Your listing is hidden</strong> and not visible to others.{" "}
 *     <Link href="/pro/dashboard">Go to dashboard</Link> to make it public.
 *   </AlertBanner>
 *
 * Variants:
 *   warning  — amber  (default for advisories)
 *   info     — blue   (neutral information)
 *   success  — green  (confirmations)
 *   error    — red    (critical problems)
 */

type Variant = "warning" | "info" | "success" | "error";

const STYLES: Record<Variant, { wrapper: string; icon: string }> = {
  warning: {
    wrapper: "bg-amber-tint border-b border-amber-200 text-amber-800",
    icon:    "text-amber-500",
  },
  info: {
    wrapper: "bg-blue-50 border-b border-blue-200 text-blue-800",
    icon:    "text-blue-500",
  },
  success: {
    wrapper: "bg-emerald-50 border-b border-emerald-200 text-emerald-800",
    icon:    "text-emerald-500",
  },
  error: {
    wrapper: "bg-red-50 border-b border-red-200 text-red-800",
    icon:    "text-red-500",
  },
};

interface AlertBannerProps {
  variant?: Variant;
  icon?: ReactNode;
  children: ReactNode;
  /**
   * `alert` is ASSERTIVE: a screen reader interrupts whatever it is reading to
   * announce it. That is right for "your listing is hidden" and wrong for a
   * passive statement of fact, which should wait its turn. Pass `status` for
   * anything the reader did not just cause and does not have to act on.
   */
  role?: "alert" | "status";
}

export default function AlertBanner({
  variant = "warning",
  icon,
  children,
  role = "alert",
}: AlertBannerProps) {
  const styles = STYLES[variant];

  return (
    <div role={role} className={`w-full ${styles.wrapper}`}>
      <div className="mx-auto flex max-w-[1280px] items-center gap-[6px] px-13 py-[9px] text-left text-xs sm:justify-center sm:px-21 sm:py-[10px] sm:text-center">
        {icon && (
          <span className={`shrink-0 ${styles.icon}`} aria-hidden="true">
            {icon}
          </span>
        )}
        <span className="leading-snug">{children}</span>
      </div>
    </div>
  );
}
